import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { PartyPopper, BedDouble, CheckCircle2, Music, Mic } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SectionHeading } from "./Story";
import { DatePicker } from "./DatePicker";
import { createRsvp } from "@/lib/api";

const arrivalTimes = ["Morning (before 12 PM)", "Afternoon (12 - 4 PM)", "Evening (4 - 8 PM)", "Night (after 8 PM)"];

const empty = {
  name: "", whatsapp: "", attending: "yes", party_size: 1,
  needs_accommodation: null, arrival_date: "", arrival_time: "", departure_date: "",
  dietary: "", notes: "", join_open_mic: false, contribution: "", prep_notes: "", custom: {},
};

export const RsvpForm = ({ content }) => {
  const deadline = content.rsvp_deadline;
  const community = content.community;
  const extraFields = content.rsvp_extra_fields;
  const success = content.rsvp_success || {};
  const accommodationInfo = content.accommodation_info;

  // Stay date pickers are limited to the first week of December (1–7) of the wedding year.
  const weddingYear = (() => {
    const y = new Date(content.wedding_date).getFullYear();
    return Number.isNaN(y) ? new Date().getFullYear() : y;
  })();
  const stayMin = new Date(weddingYear, 11, 1);
  const stayMax = new Date(weddingYear, 11, 7);

  const [form, setForm] = useState(empty);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [editsLeft, setEditsLeft] = useState(null);
  const [dup, setDup] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setCustom = (k, v) => setForm((f) => ({ ...f, custom: { ...f.custom, [k]: v } }));

  const doSubmit = async (confirm) => {
    setSubmitting(true);
    try {
      await createRsvp({ ...form, party_size: Number(form.party_size) || 1, needs_accommodation: !!form.needs_accommodation, confirm_update: confirm });
      setDup(null);
      setDone(true);
      toast.success(confirm ? "Your RSVP has been updated. 💛" : "Thank you! Your RSVP has been received. 💛");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && detail?.code === "already_exists") {
        setDup(detail);
      } else if (err?.response?.status === 409 && detail?.code === "limit_reached") {
        toast.error("You've already updated your RSVP the maximum of 2 times. Please message us on WhatsApp for any further changes.");
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.whatsapp) {
      toast.error("Please fill in your name and WhatsApp number.");
      return;
    }
    if (form.attending === "yes" && form.needs_accommodation === null) {
      toast.error("Please let us know if you need accommodation.");
      return;
    }
    if (form.attending === "yes" && form.needs_accommodation) {
      if (!form.arrival_date) {
        toast.error("Please select your arrival date so we can plan your stay.");
        return;
      }
      if (!form.arrival_time) {
        toast.error("Please select your arrival time so we can plan your stay.");
        return;
      }
    }
    doSubmit(isUpdating);
  };

  const loadForEdit = () => {
    const r = dup.rsvp || {};
    setForm({
      name: r.name || "", whatsapp: r.whatsapp || "", attending: r.attending || "yes",
      party_size: r.party_size || 1,
      needs_accommodation: typeof r.needs_accommodation === "boolean" ? r.needs_accommodation : null,
      arrival_date: r.arrival_date || "", arrival_time: r.arrival_time || "", departure_date: r.departure_date || "",
      dietary: r.dietary || "", notes: r.notes || "", join_open_mic: !!r.join_open_mic,
      contribution: r.contribution || "", prep_notes: r.prep_notes || "", custom: r.custom || {},
    });
    setIsUpdating(true);
    setEditsLeft(dup.edits_remaining);
    setDup(null);
    toast.info("We've loaded your details — edit anything and resubmit to update.");
  };

  const attending = form.attending === "yes";

  return (
    <section id="rsvp" className="py-20 sm:py-28 lg:py-32 bg-[var(--ivory)]" data-testid="rsvp-section">
      <div className="max-w-2xl mx-auto px-4 sm:px-8">
        <SectionHeading script="join us" title="RSVP" testid="rsvp-heading" />

        {done ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
            className="text-center bg-[var(--card)] rounded-2xl p-8 sm:p-10 border border-[var(--border)] shadow-sm"
            data-testid="rsvp-success"
          >
            <PartyPopper className="w-10 h-10 mx-auto text-[var(--gold)]" />
            <h3 className="font-serif text-3xl text-[var(--burgundy)] mt-4" data-testid="rsvp-success-title">
              {attending ? (success.title || "You're on the list! 💛") : "Thank you for letting us know"}
            </h3>
            <p className="text-[var(--text-secondary)] mt-3 leading-relaxed whitespace-pre-line" data-testid="rsvp-success-message">
              {attending
                ? (success.message || "Thank you for saying yes to celebrating with us. We'll be in touch soon with all the little details to make your visit comfortable.")
                : "We'll miss you dearly, but thank you so much for taking the time to respond. Sending you all our love."}
            </p>
            <button
              onClick={() => { setForm(empty); setDone(false); setIsUpdating(false); setEditsLeft(null); setDup(null); }}
              className="mt-6 text-sm underline text-[var(--gold-hover)]"
              data-testid="rsvp-another-button"
            >
              Submit another response
            </button>
          </motion.div>
        ) : (
          <form onSubmit={submit} className="bg-[var(--card)] rounded-2xl p-6 sm:p-10 border border-[var(--border)] shadow-sm space-y-6" data-testid="rsvp-form">
            {deadline && (
              <p className="text-center text-xs text-[var(--text-muted)] tracking-wide">
                Kindly respond by {new Date(deadline).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
              </p>
            )}

            {isUpdating && (
              <div className="rounded-xl border-2 border-[var(--border-gold)] bg-[var(--surface)] px-4 py-3 text-sm text-[var(--text-secondary)]" data-testid="rsvp-updating-banner">
                You're updating your existing RSVP{editsLeft != null ? ` — ${editsLeft} update${editsLeft === 1 ? "" : "s"} left after this` : ""}.
              </div>
            )}

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Full Name *</Label>
                <Input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Your name" className="mt-1.5" data-testid="rsvp-form-name-input" />
              </div>
              <div>
                <Label>WhatsApp Number *</Label>
                <Input type="tel" value={form.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} placeholder="+91 98765 43210" className="mt-1.5" data-testid="rsvp-form-whatsapp-input" />
              </div>
              <div className="sm:col-span-2">
                <Label>Number of Guests</Label>
                <Input type="number" min="1" value={form.party_size} onChange={(e) => set("party_size", e.target.value)} className="mt-1.5" data-testid="rsvp-form-party-input" />
              </div>
            </div>

            <div>
              <Label>Will you be attending? *</Label>
              <Select value={form.attending} onValueChange={(v) => set("attending", v)}>
                <SelectTrigger className="mt-1.5" data-testid="rsvp-form-attendance-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="yes" data-testid="rsvp-attend-yes">Joyfully accepts 🎉</SelectItem>
                  <SelectItem value="no" data-testid="rsvp-attend-no">Regretfully declines</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {attending && (
              <>
                {/* Explicit accommodation choice — the core of the RSVP */}
                <div
                  className="rounded-2xl p-5 sm:p-6 border-2"
                  style={{ borderColor: "var(--border-gold)", backgroundColor: "var(--surface)" }}
                  data-testid="rsvp-accommodation-block"
                >
                  <div className="flex items-center gap-2">
                    <BedDouble className="w-5 h-5 text-[var(--gold)]" />
                    <p className="font-serif text-xl sm:text-2xl text-[var(--burgundy)]">Do you need accommodation?</p>
                  </div>
                  <p className="text-sm text-[var(--text-secondary)] mt-1 mb-4">
                    This is important — it helps us reserve rooms and plan your stay. Please choose one.
                  </p>

                  <div className="grid sm:grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => set("needs_accommodation", true)}
                      data-testid="rsvp-form-accommodation-toggle"
                      className="text-left rounded-xl p-4 border-2 transition-all hover:scale-[1.01]"
                      style={{
                        borderColor: form.needs_accommodation === true ? "var(--gold)" : "var(--border)",
                        backgroundColor: "var(--card)",
                        boxShadow: form.needs_accommodation === true ? "0 6px 20px rgba(200,150,62,0.25)" : "none",
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-[var(--text-primary)]">Yes, I'll need a room</span>
                        {form.needs_accommodation === true && <CheckCircle2 className="w-5 h-5 text-[var(--gold)]" />}
                      </div>
                      <span className="text-xs text-[var(--text-secondary)] mt-1 block">We'll arrange & confirm your stay.</span>
                    </button>

                    <button
                      type="button"
                      onClick={() => set("needs_accommodation", false)}
                      data-testid="rsvp-form-accommodation-no"
                      className="text-left rounded-xl p-4 border-2 transition-all hover:scale-[1.01]"
                      style={{
                        borderColor: form.needs_accommodation === false ? "var(--gold)" : "var(--border)",
                        backgroundColor: "var(--card)",
                        boxShadow: form.needs_accommodation === false ? "0 6px 20px rgba(200,150,62,0.25)" : "none",
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-[var(--text-primary)]">No, I'll arrange my own</span>
                        {form.needs_accommodation === false && <CheckCircle2 className="w-5 h-5 text-[var(--gold)]" />}
                      </div>
                      <span className="text-xs text-[var(--text-secondary)] mt-1 block">Thanks for letting us know.</span>
                    </button>
                  </div>

                  {form.needs_accommodation === true && (
                    <motion.div
                      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                      className="mt-5 rounded-xl p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
                      style={{ backgroundColor: "var(--rose)" }}
                      data-testid="rsvp-arrival-fields"
                    >
                      <div className="min-w-0">
                        <Label className="text-[var(--burgundy)]">Date of Arrival *</Label>
                        <DatePicker value={form.arrival_date} onChange={(v) => set("arrival_date", v)} placeholder="Select arrival date" testid="rsvp-form-arrival-date-input" minDate={stayMin} maxDate={stayMax} />
                      </div>
                      <div className="min-w-0">
                        <Label className="text-[var(--burgundy)]">Time of Arrival *</Label>
                        <Select value={form.arrival_time} onValueChange={(v) => set("arrival_time", v)}>
                          <SelectTrigger className="mt-1.5 w-full" data-testid="rsvp-form-arrival-time-input">
                            <SelectValue placeholder="Select time" />
                          </SelectTrigger>
                          <SelectContent>
                            {arrivalTimes.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="min-w-0 sm:col-span-2 lg:col-span-1">
                        <Label className="text-[var(--burgundy)]">Date of Departure</Label>
                        <DatePicker value={form.departure_date} onChange={(v) => set("departure_date", v)} placeholder="Select departure date" testid="rsvp-form-departure-date-input" minDate={stayMin} maxDate={stayMax} />
                      </div>
                      {accommodationInfo && (
                        <div className="sm:col-span-2 lg:col-span-3 rounded-lg bg-[var(--card)] border border-[var(--border-gold)] px-3 py-2 flex items-start gap-2" data-testid="rsvp-accommodation-info-below">
                          <BedDouble className="w-3.5 h-3.5 text-[var(--gold)] shrink-0 mt-0.5" />
                          <p className="text-xs text-[var(--text-muted)] leading-relaxed">{accommodationInfo}</p>
                        </div>
                      )}
                    </motion.div>
                  )}
                </div>

                <div>
                  <Label>Dietary Restrictions</Label>
                  <Input value={form.dietary} onChange={(e) => set("dietary", e.target.value)} placeholder="Vegetarian, allergies, etc." className="mt-1.5" data-testid="rsvp-form-dietary-input" />
                </div>

                {/* Community open mic opt-in (only if enabled from CMS) */}
                {community?.enabled && (
                <div
                  className="rounded-2xl p-5 sm:p-6 border-2"
                  style={{ borderColor: "var(--border-gold)", backgroundColor: "var(--surface)" }}
                  data-testid="rsvp-openmic-block"
                >
                  <div className="flex items-center gap-2">
                    <Music className="w-5 h-5 text-[var(--gold)]" />
                    <p className="font-serif text-xl sm:text-2xl text-[var(--burgundy)]">Join the open mic?</p>
                  </div>
                  <p className="text-sm text-[var(--text-secondary)] mt-1 mb-4">
                    {community?.nudge || "Sing, strum a guitar, blow a kazoo, or read a poem — the stage is yours. We'd love you to be part of it!"}
                  </p>
                  <button
                    type="button"
                    onClick={() => set("join_open_mic", !form.join_open_mic)}
                    data-testid="rsvp-openmic-toggle"
                    className="rounded-xl px-4 py-3 border-2 transition-all hover:scale-[1.01] flex items-center gap-2"
                    style={{
                      borderColor: form.join_open_mic ? "var(--gold)" : "var(--border)",
                      backgroundColor: "var(--card)",
                      boxShadow: form.join_open_mic ? "0 6px 20px rgba(200,150,62,0.25)" : "none",
                    }}
                  >
                    {form.join_open_mic ? <CheckCircle2 className="w-5 h-5 text-[var(--gold)]" /> : <Mic className="w-5 h-5 text-[var(--gold)]" />}
                    <span className="font-medium text-[var(--text-primary)]">{form.join_open_mic ? "Yes, count me in!" : "Yes, I'd love to join!"}</span>
                  </button>
                  {form.join_open_mic && (
                    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mt-4" data-testid="rsvp-contribution-field">
                      <Label>What would you love to do? (song, guitar, kazoo, poem, dance...)</Label>
                      <Input value={form.contribution} onChange={(e) => set("contribution", e.target.value)} placeholder="e.g., Acoustic cover of our favourite song 🎸" className="mt-1.5" data-testid="rsvp-form-contribution-input" />
                    </motion.div>
                  )}
                </div>
                )}

                <div>
                  <Label>Anything we should know to host you better?</Label>
                  <Textarea value={form.prep_notes} onChange={(e) => set("prep_notes", e.target.value)} placeholder="Allergies, accessibility needs, travelling with kids/elders, early check-in..." className="mt-1.5" data-testid="rsvp-form-prep-notes-input" />
                </div>
              </>
            )}

            {(extraFields || []).map((f) => (
              <div key={f.id}>
                <Label>{f.label}</Label>
                {f.type === "textarea"
                  ? <Textarea value={form.custom[f.label] || ""} onChange={(e) => setCustom(f.label, e.target.value)} className="mt-1.5" data-testid={`rsvp-custom-${f.id}`} />
                  : <Input value={form.custom[f.label] || ""} onChange={(e) => setCustom(f.label, e.target.value)} className="mt-1.5" data-testid={`rsvp-custom-${f.id}`} />}
              </div>
            ))}

            <div>
              <Label>Message for the couple</Label>
              <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="A note, a wish, a song request..." className="mt-1.5" data-testid="rsvp-form-notes-input" />
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full rounded-full py-3.5 font-medium transition-all hover:scale-[1.02] disabled:opacity-60"
              style={{ backgroundColor: "var(--gold)", color: "var(--on-gold)" }}
              data-testid="rsvp-form-submit-button"
            >
              {submitting ? "Sending..." : isUpdating ? "Update my RSVP" : "Send my RSVP"}
            </button>
          </form>
        )}
      </div>

      {dup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="rsvp-duplicate-modal">
          <div className="bg-[var(--card)] rounded-2xl p-6 max-w-md w-full border border-[var(--border)] shadow-xl">
            <h3 className="font-serif text-2xl text-[var(--burgundy)]">You've already RSVP'd 💛</h3>
            <p className="text-sm text-[var(--text-secondary)] mt-2 leading-relaxed">
              We found a response under this WhatsApp number{dup.name ? ` for ${dup.name}` : ""}.
              {dup.edits_remaining > 0
                ? ` You can update your details ${dup.edits_remaining} more time${dup.edits_remaining === 1 ? "" : "s"}.`
                : " You've reached the maximum of 2 updates — please message us on WhatsApp for any further changes."}
            </p>
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => setDup(null)} className="px-4 py-2 rounded-full border border-[var(--border)] text-sm text-[var(--text-secondary)]" data-testid="rsvp-duplicate-cancel">Close</button>
              {dup.edits_remaining > 0 ? (
                <button onClick={loadForEdit} className="px-5 py-2 rounded-full text-sm font-medium" style={{ backgroundColor: "var(--gold)", color: "var(--on-gold)" }} data-testid="rsvp-duplicate-edit">Load &amp; edit my details</button>
              ) : (
                <button onClick={() => setDup(null)} className="px-5 py-2 rounded-full text-sm font-medium" style={{ backgroundColor: "var(--gold)", color: "var(--on-gold)" }} data-testid="rsvp-duplicate-ok">Okay</button>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};
